import React, { useState, useEffect, useCallback } from "react";
import "./../../../assets/css/purchase/form.css";

// React DateTime
import moment from "moment";

// Components
import Breadcrumbs from "../../components/Breadcrumbs";
import Sidebar from "../../components/Sidebar";
import ReportsTable from "../../components/Content/ReportsTable";
import DatePicker from "../../components/DatePicker";
import ArModal from "../../components/Modal/ArModal";
import DeliveryModal from "../../components/Modal/DeliveryModal";

// Utils
import {
  getAlert,
  getDataWithComp,
  getSoWithoutPK,
} from "../../../utils/SamuiUtils";
import {
  useDataTableKeyboardNavigation,
  getDataTableRowStyle,
  getDataTableKeyboardInstructions
} from '../../../utils/DataTableKeyboardNavigation';

const SalesReport = () => {
  // ตัวแปรเก็บชื่อหน้าจอ
  const [name, setName] = useState(null);

  // ตัวแปรเก็บค่าเงื่อนไขการค้นหา
  const [searchCriteria, setSearchCriteria] = useState([]);

  // ข้อมูลที่แสดงรายงาน
  const [filteredData, setFilteredData] = useState([]);

  // สำหรับ Modal ลูกค้า
  const [showArModal, setShowArModal] = useState(false);
  const [arDataList, setArDataList] = useState([]);
  const [customerSelected, setCustomerSelected] = useState({
    AR_Code: "",
    AR_Name: "",
  });

  // สำหรับ Modal สายส่ง
  const [showSoModal, setShowSoModal] = useState(false);
  const [soDataList, setSoDataList] = useState([]);
  const [dlLineList, setDlLineList] = useState([]);
  const [dlSelected, setDlSelected] = useState({ DL_Id: "", DL_LineName: "" });

  // สำหรับ Table รายงาน
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(16);
  const [selectedRowIndex, setSelectedRowIndex] = useState(-1);
  const [exportController, setExportController] = useState({
    exportToExcel: null,
    isExporting: false,
    hasData: false,
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
  useEffect(() => {
    initialize();
  }, []);

  // Page change handler
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    setSelectedRowIndex(-1);
  };

  // Row selection handler
  const onRowSelected = (item, index) => {
    setSelectedRowIndex(index);
    // เพิ่มการกระทำอื่นๆ ตามต้องการ เช่น แสดงรายละเอียด
  };

  const initialize = async () => {
    // เริ่มต้นหน้าจอ (เปิดครั้งแรก)
    setName("1. รายงานยอดซื้อ / ขายประจำวัน");
    setSearchCriteria({
      docDate: moment().format("YYYY-MM-DD"), // แก้ไขให้เป็น date format เท่านั้น
      docIsPickingList: null,
      docStatusPaidType: null,
      docStatusPaid: null, // เพิ่มสถานะจ่าย
    });
    setFilteredData([]);
    setCustomerSelected({ AR_Code: "", AR_Name: "" });
    setDlSelected({ DL_Id: "", DL_LineName: "" });

    // Load รายชื่อลูกค้า
    const customerDataList = await getDataWithComp(
      "Tb_Set_AR",
      "AND AR_Status = 'Y' ORDER By AR_Code ASC"
    );
    if (customerDataList && customerDataList.length > 0) {
      setArDataList(customerDataList);
    }

    // Load สายส่ง
    const dlDataList = await getDataWithComp("DL_Line_H", "ORDER By DL_Id ASC");
    if (dlDataList && dlDataList.length > 0) {
      setDlLineList(dlDataList);
    }

    // Load SO Data
    const soDataList = await getSoWithoutPK();
    if (soDataList && soDataList.length > 0) {
      setSoDataList(soDataList);
    }
  };

  const handleChangeCriteria = (e) => {
    const { name, value } = e.target;
    setSearchCriteria((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleChangeDate = (value, name) => {
    setSearchCriteria((prev) => ({
      ...prev,
      [name]: value ? value.format("YYYY-MM-DD") : null,
    }));
  };

  const handleRadioChange = (e) => {
    const { name, value } = e.target;
    setSearchCriteria((prev) => ({
      ...prev,
      [name]: value === "" ? null : parseInt(value),
    }));
  };

  // ฟังก์ชันสำหรับ Modal ลูกค้า
  const handleArShow = () => setShowArModal(true);
  const handleArClose = () => setShowArModal(false);

  const onRowSelectAr = async (customerSelected) => {
    try {
      setCustomerSelected(customerSelected);
      handleArClose();
    } catch (error) {
      getAlert("FAILED", error.message || error);
    }
  };

  // ฟังก์ชันสำหรับ Modal สายส่ง
  const handleSoShow = () => setShowSoModal(true);
  const handleSoClose = () => setShowSoModal(false);

  const onRowSelectDL = async (dlSelected) => {
    try {
      setDlSelected(dlSelected);
      handleSoClose();
    } catch (error) {
      getAlert("FAILED", error.message || error);
    }
  };

  const handleSearch = async () => {
    try {
      setFilteredData([]);

      let query = "";

      if (searchCriteria.docDate) {
        query += `AND (CAST(Doc_Date AS DATE) = '${searchCriteria.docDate}') `;
      }

      // เพิ่มเงื่อนไขลูกค้า
      if (customerSelected?.AR_Code) {
        query += `AND AR_Code = '${customerSelected.AR_Code}' `;
      }

      // เพิ่มเงื่อนไขสายส่ง
      if (dlSelected?.DL_Id) {
        query += `AND EXISTS (
                    SELECT 1 FROM API_0801_PKList_H PK 
                    LEFT JOIN DL_Line_H DL ON PK.Delivery_Line_Id = DL.DL_Id
                    WHERE SOH.Doc_Is_PickingList = PK.Doc_Id 
                    AND DL.DL_Id = '${dlSelected.DL_Id}'
                ) `;
      }

      if (
        searchCriteria.docIsPickingList !== null &&
        searchCriteria.docIsPickingList !== undefined
      ) {
        query +=
          searchCriteria.docIsPickingList === 1
            ? "AND Doc_Is_PickingList = 0 "
            : "AND Doc_Is_PickingList <> 0 ";
      }

      if (searchCriteria.docStatusPaidType) {
        query += `AND Doc_Status_Paid_Type = ${searchCriteria.docStatusPaidType} `;
      }

      // เพิ่มเงื่อนไขสถานะจ่าย
      if (
        searchCriteria.docStatusPaid !== null &&
        searchCriteria.docStatusPaid !== undefined
      ) {
        query += `AND Doc_Status_Paid = ${searchCriteria.docStatusPaid} `;
      }

      // เพิ่ม Company ID
      const companyId = window.localStorage.getItem("company") || 1;
      query += `AND (Comp_Id = ${companyId}) `;

      // ไม่เอาใบขายที่ยกเลิกไปแล้ว
      query += `AND Doc_Status <> '13'`;

      // เรียก API โดยไม่ใส่ selectFields เพื่อให้ได้ข้อมูลครบ
      const response = await getDataWithComp("API_0701_SO_H SOH", query.trim());

      if (response && response.length > 0) {
        // Process ข้อมูลให้มี SO_Type ที่ถูกต้อง
        const processedData = response.map((item) => ({
          ...item,
          SO_Type: item.Doc_Is_PickingList === 0 ? "Admin" : "Vansale",
        }));

        setFilteredData(processedData);
      } else {
        setFilteredData([]);
      }
    } catch (error) {
      getAlert("FAILED", error.response?.message || error.message);
    }
  };

  const handleExportReady = useCallback((state) => {
    setExportController((prev) => {
      if (
        prev.exportToExcel === state.exportToExcel &&
        prev.isExporting === state.isExporting &&
        prev.hasData === state.hasData
      ) {
        return prev;
      }
      return state;
    });
  }, []);

  const handleExportClick = useCallback(() => {
    if (exportController?.exportToExcel) {
      exportController.exportToExcel();
    } else {
      getAlert("FAILED", "ไม่มีข้อมูลสำหรับ Export");
    }
  }, [exportController]);

  // Field mappings for keyboard navigation
  const fieldMappings = [
    { field: 'Doc_No', label: 'เลขเอกสาร' },
    { field: 'Doc_Date', label: 'วันที่' },
    { field: 'AR_Name', label: 'ลูกค้า' }
  ];

  // Keyboard navigation
  useDataTableKeyboardNavigation(
    true, // isActive
    currentItems,
    selectedRowIndex,
    setSelectedRowIndex,
    onRowSelected,
    fieldMappings,
    currentPage,
    handlePageChange,
    filteredData.length,
    itemsPerPage
  );

  return (
    <>
      <Sidebar />
      <div className="main-panel">
        <div className="container">
          <div className="page-inner">
            <div className="page-header d-flex justify-content-between align-items-center">
              <Breadcrumbs
                page={name}
                items={[
                  { name: "รายงาน", url: "/reports" },
                  { name: name, url: "/sales-report" },
                ]}
              />
            </div>
            <div className="row">
              {/* Criteria */}
              <div className="col-md-12">
                <div className="card">
                  <div className="card-header">
                    <h4>เงื่อนไขการค้นหา</h4>
                  </div>
                  <div className="card-body">
                    <div className="row g-3">
                      <div className="col-md-2">
                        <label htmlFor="docDate" className="form-label">
                          วันที่เอกสาร
                        </label>
                        <DatePicker
                          label=""
                          name="docDate"
                          value={searchCriteria.docDate}
                          onChange={(date) => handleChangeDate(date, "docDate")}
                          disabled={false}
                        />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label">ลูกค้า</label>
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control input-spacing"
                            name="customerName"
                            value={
                              customerSelected?.AR_Code
                                ? `${customerSelected.AR_Code} - ${customerSelected.AR_Name}`
                                : ""
                            }
                            disabled={true}
                          />
                          <button
                            className="btn btn-outline-secondary"
                            onClick={(e) => {
                              e.preventDefault();
                              handleArShow();
                            }}
                          >
                            <i className="fas fa-search"></i>
                          </button>
                        </div>
                      </div>
                      <div className="col-md-3">
                        <label className="form-label">สายส่ง</label>
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control input-spacing"
                            name="dlLineName"
                            value={dlSelected?.DL_LineName || ""}
                            disabled={true}
                          />
                          <button
                            className="btn btn-outline-secondary"
                            onClick={(e) => {
                              e.preventDefault();
                              handleSoShow();
                            }}
                          >
                            <i className="fas fa-search"></i>
                          </button>
                        </div>
                      </div>

                      <div className="col-md-3 mb-4">
                        <label
                          htmlFor="docStatusPaidType"
                          className="form-label"
                        >
                          ประเภทการจ่าย
                        </label>
                        <select
                          className="form-select form-control input-spacing"
                          id="docStatusPaidType"
                          name="docStatusPaidType"
                          value={searchCriteria.docStatusPaidType || ""}
                          onChange={handleChangeCriteria}
                        >
                          <option value="">ทั้งหมด</option>
                          <option value={1}>เงินสด</option>
                          <option value={2}>โอน</option>
                          <option value={3}>เช็ค</option>
                          <option value={4}>เครดิต</option>
                          <option value={5}>ค้างจ่าย</option>
                          <option value={6}>หลายช่องทาง</option>
                        </select>
                      </div>

                      <div className="col-md-3">
                        <label className="form-label">ประเภท</label>
                        <div className="mt-2">
                          <div className="form-check form-check-inline">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="docIsPickingList"
                              id="typeAll"
                              value=""
                              checked={searchCriteria.docIsPickingList === null}
                              onChange={handleRadioChange}
                            />
                            <label
                              className="form-check-label w-50"
                              htmlFor="typeAll"
                            >
                              ทั้งหมด
                            </label>
                          </div>
                          <div className="form-check form-check-inline">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="docIsPickingList"
                              id="typeAdmin"
                              value="1"
                              checked={searchCriteria.docIsPickingList === 1}
                              onChange={handleRadioChange}
                            />
                            <label
                              className="form-check-label w-50"
                              htmlFor="typeAdmin"
                            >
                              Admin
                            </label>
                          </div>
                          <div className="form-check form-check-inline">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="docIsPickingList"
                              id="typeVansale"
                              value="2"
                              checked={searchCriteria.docIsPickingList === 2}
                              onChange={handleRadioChange}
                            />
                            <label
                              className="form-check-label w-50"
                              htmlFor="typeVansale"
                            >
                              Vansale
                            </label>
                          </div>
                        </div>
                      </div>

                      {/* เพิ่มสถานะจ่าย Radio */}
                      <div className="col-md-4">
                        <label className="form-label">สถานะจ่าย</label>
                        <div className="mt-2">
                          <div className="form-check form-check-inline">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="docStatusPaid"
                              id="statusPaidAll"
                              value=""
                              checked={searchCriteria.docStatusPaid === null}
                              onChange={handleRadioChange}
                            />
                            <label
                              className="form-check-label w-25"
                              htmlFor="statusPaidAll"
                            >
                              ทั้งหมด
                            </label>
                          </div>
                          <div className="form-check form-check-inline">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="docStatusPaid"
                              id="statusPaidPending"
                              value="2"
                              checked={searchCriteria.docStatusPaid === 2}
                              onChange={handleRadioChange}
                            />
                            <label
                              className="form-check-label w-25"
                              htmlFor="statusPaidPending"
                            >
                              ค้างจ่าย
                            </label>
                          </div>
                          <div className="form-check form-check-inline">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="docStatusPaid"
                              id="statusPaidComplete"
                              value="3"
                              checked={searchCriteria.docStatusPaid === 3}
                              onChange={handleRadioChange}
                            />
                            <label
                              className="form-check-label w-25"
                              htmlFor="statusPaidComplete"
                            >
                              จ่ายครบ
                            </label>
                          </div>
                        </div>
                      </div>

                      <div className="col-md-3"></div>

                      <div className="col-md-12 d-flex justify-content-end">
                        <button
                          type="button"
                          className="btn btn-lg shadow text-white"
                          style={{
                            width: "10%",
                            marginRight: "20px",
                            backgroundColor: "#EF6C00",
                            fontSize: "16px",
                          }}
                          onClick={() => handleSearch()}
                        >
                          <i className="fas fa-search" /> ค้นหา
                        </button>
                        <button
                          type="button"
                          className="btn btn-lg shadow text-white"
                          style={{
                            width: "10%",
                            marginRight: "20px",
                            backgroundColor: "#EF6C00",
                            fontSize: "16px",
                          }}
                          onClick={handleExportClick}
                          disabled={!exportController.hasData || exportController.isExporting}
                        >
                          <i className="fas fa-file-excel" />{" "}
                          {exportController.isExporting ? "กำลังส่งออก..." : "Export"}
                        </button>
                        <button
                          type="button"
                          className="btn btn-lg shadow text-white"
                          style={{
                            width: "10%",
                            backgroundColor: "#EF6C00",
                            fontSize: "16px",
                          }}
                          onClick={() => initialize()}
                        >
                          <i className="fas fa-undo" /> เริ่มใหม่
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* AR Modal */}
              <ArModal
                showArModal={showArModal}
                handleArClose={handleArClose}
                arDataList={arDataList}
                onRowSelectAr={onRowSelectAr}
              />
              {/* Delivery Modal for สายส่ง */}
              <DeliveryModal
                showSoModal={showSoModal}
                handleSoClose={handleSoClose}
                soDataList={soDataList}
                dlLineList={dlLineList}
                onRowSelectDL={onRowSelectDL}
              />
              {/* ReportsTable */}
              <ReportsTable
                currentItems={currentItems}
                currentPage={currentPage}
                handlePageChange={handlePageChange}
                dataMasterList={filteredData}
                itemsPerPage={itemsPerPage}
                setCurrentPage={setCurrentPage}
                setItemsPerPage={setItemsPerPage}
                selectedRowIndex={selectedRowIndex}
                setSelectedRowIndex={setSelectedRowIndex}
                onRowSelected={onRowSelected}
                fieldMappings={{
                  Doc_No: "Doc_No",
                  SO_Type: "SO_Type",
                  DocStatusPaid_Name: "DocStatusPaid_Name",
                  DocStatusPaid_Colour: "DocStatusPaid_Colour",
                  DocStatusPaid_Type_Name: "DocStatusPaid_Type_Name",
                  DocStatusPaidType_Colour: "DocStatusPaidType_Colour",
                  AR_ID: "AR_ID",
                  AR_Name: "AR_Name",
                  DL_LineName: "DL_LineName",
                  Total_Before_Vat: "Total_Before_Vat",
                  VAT: "VAT",
                  NetTotal: "NetTotal",
                  Pay_Total: "Pay_Total",
                }}
                onExportReady={handleExportReady}
                showExportButton={false}
              />

              {/* Keyboard Instructions */}
              <div className="text-muted" style={{ fontSize: "12px" }}>
                {getDataTableKeyboardInstructions(false, true)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SalesReport;
